/**
 * State Flow Editor Component
 * 
 * Component for editing state actions in operational and improvement flows.
 * Displays both flows with action selectors for each state.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Space, Typography, Divider, Alert } from 'antd';
import { StateFlowVisual } from './StateFlowVisual';
import {
  StandardOperationalState,
  StandardImprovementState,
  getStatesForType
} from '../../types/standardStates';
import { StateActions } from '../../types/globalProperties';

const { Title, Text } = Typography;

interface StateActionInfo {
  forward?: string; // StateAction as string
  backward?: string; // StateAction as string
  both?: string; // StateAction as string
}

interface StateFlowEditorProps {
  value?: StateActions;
  onChange?: (value: StateActions) => void;
  onStateClick?: (state: string, flowType: 'operational' | 'improvement') => void;
  stateActionDetails?: Record<string, Record<string, StateActionInfo>>; // flowType -> state -> details
  editable?: boolean;
}


export const StateFlowEditor: React.FC<StateFlowEditorProps> = ({
  value,
  onChange,
  onStateClick,
  stateActionDetails,
  editable = true
}) => {
  const [stateActions, setStateActions] = useState<StateActions>(value || {
    operational: {},
    improvement: {}
  });
  const prevValueRef = useRef<string>('');

  useEffect(() => {
    // Use JSON.stringify to detect deep changes in the value object
    const currentValueStr = JSON.stringify(value || { operational: {}, improvement: {} });
    if (currentValueStr !== prevValueRef.current) {
      prevValueRef.current = currentValueStr;
      // Always update when value changes, even if undefined
      setStateActions(value || {
        operational: {},
        improvement: {}
      });
    }
  }, [value]);

  const operationalStates = getStatesForType('operational');
  const improvementStates = getStatesForType('improvement');

  return (
    <div className="state-flow-editor">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Operational Flow */}
        <div>
          <Title level={5}>Operational Flow</Title>
          {editable && (
            <Alert
              message="Click on a state box to configure its action and change type"
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}
          <StateFlowVisual
            states={operationalStates}
            stateActions={stateActions.operational || {}}
            stateActionDetails={stateActionDetails?.operational}
            flowType="operational"
            editable={editable}
            onStateClick={(state, flowType) => onStateClick?.(state, flowType)}
          />
        </div>

        <Divider />

        {/* Improvement Flow */}
        <div>
          <Title level={5}>Improvement Flow</Title>
          {editable && (
            <Alert
              message="Click on a state box to configure its action and change type"
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}
          <StateFlowVisual
            states={improvementStates}
            stateActions={stateActions.improvement || {}}
            stateActionDetails={stateActionDetails?.improvement}
            flowType="improvement"
            editable={editable}
            onStateClick={(state, flowType) => onStateClick?.(state, flowType)}
          />
        </div>
      </Space>
    </div>
  );
};

